import { ArrowLeftOutlined, InfoCircleFilled } from "@ant-design/icons";
import { roundOff } from "src/utils/helperFunctions";
import { tierOf } from "./bands";

const RESULT = {
  PASS: { text: "Cleared", tone: "ok" },
  FAIL: { text: "Did not clear", tone: "bad" },
  PENDING: { text: "Result pending", tone: "quiet" },
};

/** Each bucket that has a cut-off on any of its skills, and whether all of them cleared. */
const cutoffByBucket = (buckets = []) =>
  buckets
    .map((bucket) => {
      const withCutoff = Object.values(bucket?.traitsMeta ?? {}).filter(
        (trait) => typeof trait.cutOff === "number" && trait.cutOff >= 0
      );
      if (!withCutoff.length) return null;
      return {
        id: bucket.id ?? bucket.name,
        name: (bucket.displayName || bucket.name || "").replace(/ Skills$/i, ""),
        cleared: withCutoff.every((trait) => trait.score >= trait.cutOff),
        skills: withCutoff.length,
        passed: withCutoff.filter((trait) => trait.score >= trait.cutOff).length,
      };
    })
    .filter(Boolean);

const formatCompleted = (date) =>
  `${date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}, ${date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })}`;

/**
 * The web report's header in the 4.0 hiring report's shape: the way back beside
 * the name and the result, chips for who they are, the facts in one grid, and a
 * strip that says how far the cut-off cleared. ReportHeader owns every action
 * (copy, download, proctoring) and passes them in.
 */
const HeaderProfile = ({
  reportData,
  passFailPill,
  hideVerdictPills,
  disqualifyReason,
  showBack,
  onBack,
  email,
  phone,
  chips,
  reportDuration,
  reportLanguage,
  completedDate,
  hideFitScore,
  trustScoreVal,
  isProctoringAvailable,
  candidateFormData,
  actions,
  onSkillScores,
}) => {
  const form = candidateFormData ?? {};
  const fitScore = reportData?.fitScore;
  const overall = !hideFitScore && fitScore?.score != null
    ? tierOf({
      score: fitScore.score,
      lb: fitScore.data?.lowerBound,
      ub: fitScore.data?.upperBound,
      median: fitScore.data?.median,
    })
    : null;

  const proctoring = reportData?.disqualified
    ? <span className="r4-ink-bad">Disqualified</span>
    : reportData?.trustScore?.remark
      ? `${roundOff(trustScoreVal)}% trust score`
      : isProctoringAvailable ? "Pending" : "Not proctored";

  const location = [form.currentLocation || form.city, form.state].filter(Boolean).join(", ");

  // The report's own facts always show; the application form's only when the candidate gave them.
  const facts = [
    ["Overall score", overall && (
      <span className={`r4-score r4-tile--${overall.tier}`} title={overall.label}>
        {`${roundOff(fitScore.score)} · ${overall.label}`}
      </span>
    )],
    ["Completed", completedDate && formatCompleted(completedDate)],
    ["Time taken", reportDuration],
    ["Language", reportLanguage],
    ["Proctoring", proctoring],
    ["Email", email],
    ["Phone", phone, true],
    ["Current company", form.currentCompany || form.company, true],
    ["Work experience", form.workExperience || form.yearsOfExp, true],
    ["Location", location, true],
    ["Candidate type", form.candidateType, true],
    ["Source", form.sourceOfHiring, true],
  ].filter(([, value, optional]) => !optional || value);

  const result = !hideVerdictPills && reportData?.disqualified
    ? { text: "Disqualified", tone: "bad", title: disqualifyReason }
    : passFailPill ? RESULT[passFailPill] : null;

  const strip = reportData?.cutOffCleared != null ? cutoffByBucket(reportData?.buckets) : [];
  const total = strip.reduce((sum, bucket) => sum + bucket.skills, 0);
  const passed = strip.reduce((sum, bucket) => sum + bucket.passed, 0);

  return (
    <header className="r4-head">
      <div className="r4-head__top">
        {showBack && (
          <button type="button" className="r4-head__back" onClick={onBack} aria-label="Back">
            <ArrowLeftOutlined />
          </button>
        )}
        <div className="r4-head__who">
          <div className="r4-head__name-row">
            <h1 className="r4-head__name">{reportData?.user?.name || "--"}</h1>
            {result && (
              <span className={`r4-head__result r4-bg--${result.tone}`} title={result.title}>
                {result.text}
                {result.title && <InfoCircleFilled className="ml-6" />}
              </span>
            )}
          </div>
          {chips.length > 0 && (
            <div className="r4-head__chips">
              {chips.map((chip) => <span key={chip}>{chip}</span>)}
            </div>
          )}
        </div>
        <div className="r4-head__actions">{actions}</div>
      </div>
      <dl className="r4-head__facts">
        {facts.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd title={typeof value === "string" ? value : undefined}>{value || "—"}</dd>
          </div>
        ))}
      </dl>
      {total > 0 && (
        <div className="r4-head__read">
          <b>Cut-off cleared</b>
          <span className="r4-head__bar">
            <span
              style={{ width: `${(100 * passed) / total}%` }}
              className={passed === total ? undefined : "r4-head__bar--short"}
            />
          </span>
          <b className="r4-head__pct">{`${Math.round((100 * passed) / total)}%`}</b>
          <span className="r4-head__of">{`${passed} of ${total} skills`}</span>
          <span className="r4-grow" />
          {strip.map((bucket) => (
            <span key={bucket.id} className={`r4-head__tick ${bucket.cleared ? "r4-ink-ok" : "r4-ink-bad"}`}>
              {bucket.cleared ? "✓" : "✕"} {bucket.name}
            </span>
          ))}
          {onSkillScores && (
            <button type="button" className="r4-head__more" onClick={onSkillScores}>Skill scores</button>
          )}
        </div>
      )}
    </header>
  );
};

export default HeaderProfile;
